/**
 * Admin — onglet "Apps" (Phase 10).
 * Liste des applications, modération (dépublication + flag + raison), levée du flag.
 */
import { useEffect, useMemo, useState } from 'react';
import { adminListApps, adminModerateApp, adminUnflagApp } from '@/lib/apps-api';
import { toast } from 'sonner';
import {
  Search, Globe, EyeOff, AlertTriangle, ShieldCheck, ExternalLink,
  RefreshCw, Ban, ShieldOff, Loader2,
} from 'lucide-react';

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso || ''; }
}

const statusBadge = {
  published: 'bg-emerald-500/15 text-emerald-700',
  draft: 'bg-[var(--om-ink)]/5 text-[var(--om-muted)]',
};

export default function AppsTab() {
  const [state, setState] = useState({ loading: true, items: [], total: 0, by_status: {} });
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [flagged, setFlagged] = useState(null);
  const [moderating, setModerating] = useState(null); // app id
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(null);

  const reload = async () => {
    setState((s) => ({ ...s, loading: true }));
    const res = await adminListApps({
      q,
      status: filterStatus === 'all' ? '' : filterStatus,
      flagged,
      page: 1,
      limit: 100,
    });
    setState({
      loading: false,
      items: res.ok ? res.items : [],
      total: res.ok ? res.total : 0,
      by_status: res.ok ? res.by_status : {},
    });
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filterStatus, flagged]);

  const runModerate = async () => {
    if (!moderating || !reason.trim() || reason.trim().length < 3) {
      toast.error('Raison obligatoire (3 caractères minimum).');
      return;
    }
    setPending(moderating);
    const res = await adminModerateApp(moderating, reason.trim());
    setPending(null);
    if (res.ok) {
      toast.success('Application dépubliée et flaggée.');
      setModerating(null);
      setReason('');
      reload();
    } else {
      toast.error(res.detail || 'Modération impossible.');
    }
  };

  const runUnflag = async (appId) => {
    setPending(appId);
    const res = await adminUnflagApp(appId);
    setPending(null);
    if (res.ok) {
      toast.success('Flag levé — le créateur peut republier.');
      reload();
    } else {
      toast.error(res.detail || 'Impossible de lever le flag.');
    }
  };

  const filtered = state.items;

  return (
    <div className="space-y-5" data-testid="admin-apps-tab">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-bold uppercase tracking-wider text-emerald-700">
            {state.by_status.published || 0} publiées
          </span>
          <span className="rounded-full bg-[var(--om-ink)]/5 px-2 py-0.5 font-bold uppercase tracking-wider text-[var(--om-muted)]">
            {state.by_status.draft || 0} brouillons
          </span>
          {(state.by_status.flagged || 0) > 0 && (
            <span className="rounded-full bg-rose-500/15 px-2 py-0.5 font-bold uppercase tracking-wider text-rose-700">
              {state.by_status.flagged} flaggées
            </span>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs">
            <Search size={12} strokeWidth={2.4} className="mr-1.5 text-[var(--om-muted-2)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') reload(); }}
              placeholder="Nom, slug ou email"
              className="w-52 bg-transparent focus:outline-none"
              data-testid="admin-apps-search"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] focus:outline-none"
            data-testid="admin-apps-status-filter"
          >
            <option value="all">Tous</option>
            <option value="published">Publiées</option>
            <option value="draft">Brouillons</option>
          </select>
          <button
            type="button"
            onClick={() => setFlagged(flagged === true ? null : true)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold ${
              flagged
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-700'
                : 'border-[var(--om-border)] bg-[var(--om-bg-pure)] text-[var(--om-ink)]'
            }`}
            data-testid="admin-apps-flag-filter"
          >
            <AlertTriangle size={11} strokeWidth={2.6} /> Flaggées
          </button>
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm"
            data-testid="admin-apps-refresh"
          >
            <RefreshCw size={11} strokeWidth={2.4} /> Rafraîchir
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)]">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="admin-apps-table">
            <thead className="bg-[var(--om-bg)]">
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">App</th>
                <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Créateur</th>
                <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Statut</th>
                <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Activité</th>
                <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Publiée</th>
                <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--om-border)]">
              {state.loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--om-muted)]">…</td></tr>
              )}
              {!state.loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--om-muted-2)]" data-testid="admin-apps-empty">Aucune application.</td></tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`admin-apps-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-sm font-bold text-[var(--om-ink)]">{a.name}</div>
                        <div className="text-[11px] text-[var(--om-muted-2)]">
                          <a href={`/app/${a.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                            /app/<span className="font-mono">{a.slug}</span> <ExternalLink size={9} />
                          </a>
                          {' · '}{a.blocks_count} blocs
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--om-ink)]" title={a.owner_id}>
                    <div className="truncate max-w-[200px]">{a.owner_email || '—'}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--om-muted-2)]">{a.vertical}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusBadge[a.status] || statusBadge.draft}`}>
                      {a.status === 'published' ? <Globe size={9} strokeWidth={2.6} /> : <EyeOff size={9} strokeWidth={2.6} />}
                      {a.status}
                    </span>
                    {a.is_flagged && (
                      <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">
                        <AlertTriangle size={9} strokeWidth={2.6} /> Flag
                      </div>
                    )}
                    {a.is_flagged && a.moderation_reason && (
                      <div className="mt-1 max-w-[220px] text-[10px] italic text-rose-800/80 dark:text-rose-200/80" title={a.moderation_reason}>
                        « {a.moderation_reason.length > 60 ? a.moderation_reason.slice(0, 60) + '…' : a.moderation_reason} »
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs tabular-nums text-[var(--om-ink)]">
                    <div>{a.visits_count} visites</div>
                    <div className="text-[10px] text-[var(--om-muted-2)]">
                      {a.submissions_count} form · {a.chat_messages_count} chat
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[11px] tabular-nums text-[var(--om-muted)]">
                    {a.published_at ? formatDate(a.published_at) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {a.is_flagged ? (
                        <button
                          type="button"
                          onClick={() => runUnflag(a.id)}
                          disabled={pending === a.id}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold text-emerald-700 hover:bg-emerald-500/25 disabled:opacity-50"
                          data-testid={`admin-apps-unflag-${a.id}`}
                        >
                          {pending === a.id ? <Loader2 size={10} className="animate-spin" /> : <ShieldOff size={10} strokeWidth={2.6} />}
                          Lever le flag
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setModerating(a.id); setReason(''); }}
                          className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2.5 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-500/25"
                          data-testid={`admin-apps-moderate-${a.id}`}
                        >
                          <Ban size={10} strokeWidth={2.6} /> Dépublier
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modération dialog */}
      {moderating && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="admin-moderate-dialog">
          <div className="w-full max-w-md rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-6 shadow-xl">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} strokeWidth={2.4} className="text-rose-500" />
              <h3 className="text-lg font-extrabold text-[var(--om-ink)]">Dépublier cette application ?</h3>
            </div>
            <p className="mt-2 text-sm text-[var(--om-muted)]">
              L'application repassera en brouillon et sera bloquée en publication jusqu'à ce que vous leviez le flag. Le créateur verra la raison dans son studio.
            </p>
            <label className="mt-4 block">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Raison (obligatoire, visible du créateur)</div>
              <textarea
                className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2 text-sm text-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-rose-500/20 min-h-[100px]"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="Contenu non conforme, spam, plainte utilisateur…"
                data-testid="admin-moderate-reason"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setModerating(null); setReason(''); }}
                className="rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-4 py-2 text-sm font-semibold text-[var(--om-ink)]"
                data-testid="admin-moderate-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={runModerate}
                disabled={reason.trim().length < 3 || pending}
                className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                data-testid="admin-moderate-confirm"
              >
                {pending ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} strokeWidth={2.6} />} Dépublier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
